import React, { useEffect, useState, useRef, createElement } from 'react';
import { PlayIcon, PauseIcon, VolumeIcon, Volume2Icon, VolumeXIcon, MaximizeIcon, MinimizeIcon, RotateCcwIcon, DownloadIcon } from 'lucide-react';
interface VideoMetadata {
  width?: number;
  height?: number;
  isVertical?: boolean;
  duration?: number;
}
interface ResponsiveVideoPlayerProps {
  videoUrl: string;
  title?: string;
  autoPlay?: boolean;
  loop?: boolean;
  showControls?: boolean;
  allowDownload?: boolean;
  className?: string;
  posterImage?: string;
  videoMetadata?: VideoMetadata;
}
export const ResponsiveVideoPlayer: React.FC<ResponsiveVideoPlayerProps> = ({
  videoUrl,
  title = '',
  autoPlay = false,
  loop = true,
  showControls = true,
  allowDownload = false,
  className = '',
  posterImage,
  videoMetadata
}) => {
  // Player state
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [areControlsVisible, setAreControlsVisible] = useState(true);
  const [isVertical, setIsVertical] = useState(videoMetadata?.isVertical || false);
  const [isSeeking, setIsSeeking] = useState(false);
  // References
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const volumeBarRef = useRef<HTMLDivElement>(null);
  // --- Auto-hide controls logic ---
  const startHideTimer = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) {
        setAreControlsVisible(false);
      }
    }, 3000);
  };
  const handleUserActivity = () => {
    setAreControlsVisible(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    // Only start hide timer if video is currently playing
    if (videoRef.current && !videoRef.current.paused) {
      controlsTimeoutRef.current = setTimeout(() => {
        if (videoRef.current && !videoRef.current.paused) {
          setAreControlsVisible(false);
        }
      }, 3000);
    }
  };
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);
  // Effect to handle fullscreen changes (cross-browser)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fsElement = document.fullscreenElement || (document as any).webkitFullscreenElement || (document as any).msFullscreenElement;
      setIsFullscreen(!!fsElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    // iOS Safari: listen on video element for native fullscreen events
    const video = videoRef.current;
    if (video) {
      video.addEventListener('webkitbeginfullscreen', () => setIsFullscreen(true));
      video.addEventListener('webkitendfullscreen', () => setIsFullscreen(false));
    }
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      if (video) {
        video.removeEventListener('webkitbeginfullscreen', () => setIsFullscreen(true));
        video.removeEventListener('webkitendfullscreen', () => setIsFullscreen(false));
      }
    };
  }, []);
  // Handlers
  const handlePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
      // When pausing, always show controls and clear any hide timer
      setAreControlsVisible(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
        controlsTimeoutRef.current = null;
      }
    } else {
      video.play().catch(error => {
        console.error('Error playing video:', error);
      });
      // When playing, start the hide timer
      startHideTimer();
    }
    setIsPlaying(!isPlaying);
  };
  const volumeRef = useRef(volume);
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    // Direct imperative update — no waiting for React re-render
    const video = videoRef.current;
    if (video) {
      video.volume = val;
      video.muted = val === 0;
    }
    volumeRef.current = val;
    setVolume(val);
    setIsMuted(val === 0);
  };
  const handleMuteToggle = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isMuted) {
      const restoreVolume = volumeRef.current > 0 ? volumeRef.current : 0.5;
      video.muted = false;
      video.volume = restoreVolume;
      volumeRef.current = restoreVolume;
      setVolume(restoreVolume);
      setIsMuted(false);
    } else {
      video.muted = true;
      setIsMuted(true);
    }
  };
  const getVolumeFromPointer = (clientX: number): number => {
    const bar = volumeBarRef.current;
    if (!bar) return volumeRef.current;
    const rect = bar.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  };
  const applyVolume = (val: number) => {
    const video = videoRef.current;
    if (video) {
      // Force unmute when user drags above 0
      if (val > 0) {
        video.muted = false;
      }
      video.volume = val;
      if (val === 0) {
        video.muted = true;
      }
    }
    volumeRef.current = val;
    setVolume(val);
    setIsMuted(val === 0);
  };
  const handleVolumePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const val = getVolumeFromPointer(e.clientX);
    applyVolume(val);
    const handlePointerMove = (ev: PointerEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      applyVolume(getVolumeFromPointer(ev.clientX));
    };
    const handlePointerUp = (ev: PointerEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };
  const handleTimeUpdate = () => {
    if (videoRef.current && !isSeeking) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      setDuration(video.duration);
      setIsLoaded(true);
      // Sync volume/muted state imperatively on load
      video.volume = volumeRef.current;
      video.muted = isMuted;
      // Detect orientation only if not explicitly provided in metadata
      if (videoMetadata?.isVertical === undefined || videoMetadata?.isVertical === null) {
        const detected = video.videoHeight > video.videoWidth;
        setIsVertical(detected);
      }
    }
  };
  const getSeekTimeFromPointer = (clientX: number): number => {
    const bar = progressBarRef.current;
    if (!bar || !duration) return 0;
    const rect = bar.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return fraction * duration;
  };
  const handleProgressPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSeeking(true);
    const seekTime = getSeekTimeFromPointer(e.clientX);
    setCurrentTime(seekTime);
    if (videoRef.current) {
      videoRef.current.currentTime = seekTime;
    }
    const handlePointerMove = (ev: PointerEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      const t = getSeekTimeFromPointer(ev.clientX);
      setCurrentTime(t);
      if (videoRef.current) {
        videoRef.current.currentTime = t;
      }
    };
    const handlePointerUp = (ev: PointerEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      setIsSeeking(false);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };
  const handleVideoEnded = () => {
    setIsPlaying(false);
    if (loop && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(error => {
        console.error('Error playing video:', error);
      });
      setIsPlaying(true);
    }
  };
  const handleFullscreenToggle = () => {
    const container = containerRef.current;
    const video = videoRef.current;
    if (!container && !video) return;
    const fsElement = document.fullscreenElement || (document as any).webkitFullscreenElement || (document as any).msFullscreenElement;
    if (!fsElement) {
      // Try entering fullscreen — prioritize container for custom controls
      if (container?.requestFullscreen) {
        container.requestFullscreen().catch(() => {
          // Fallback: try video element fullscreen (iOS Safari)
          if (video && (video as any).webkitEnterFullscreen) {
            ;
            (video as any).webkitEnterFullscreen();
          }
        });
      } else if (container && (container as any).webkitRequestFullscreen) {
        ;
        (container as any).webkitRequestFullscreen();
      } else if (video && (video as any).webkitEnterFullscreen) {
        // iOS Safari: only supports fullscreen on video element
        ;
        (video as any).webkitEnterFullscreen();
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        ;
        (document as any).webkitExitFullscreen();
      }
    }
  };
  const handleRestart = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      if (!isPlaying) {
        videoRef.current.play().catch(error => {
          console.error('Error playing video:', error);
        });
        setIsPlaying(true);
      }
    }
  };
  const handleDownload = async () => {
    if (videoUrl) {
      try {
        const response = await fetch(videoUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = title || 'video-download';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download failed:', error);
      }
    }
  };
  const handleWaiting = () => {
    setIsBuffering(true);
  };
  const handlePlaying = () => {
    setIsBuffering(false);
  };
  // Format time (seconds -> MM:SS)
  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  // Determine video container class based on orientation
  const videoContainerClass = isVertical ? 'vertical-video-container' : 'horizontal-video-container';
  return <div ref={containerRef} className={`relative overflow-hidden rounded-lg bg-black ${videoContainerClass} ${className}`} style={isVertical ? {
    width: '100%',
    paddingBottom: '177.78%',
    height: 0
  } : undefined} onDoubleClick={handleFullscreenToggle} onMouseMove={handleUserActivity} onTouchStart={handleUserActivity} onTouchMove={handleUserActivity} onClick={handleUserActivity} data-id="element-2933">
      {/* Video Element */}
      <video ref={videoRef} src={videoUrl} poster={posterImage} className={isVertical ? 'vertical-video' : 'horizontal-video'} style={isVertical ? {
      objectFit: 'cover' as const,
      position: 'absolute' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%'
    } : undefined} autoPlay={autoPlay} loop={loop} playsInline controls={false} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={handleVideoEnded} onWaiting={handleWaiting} onPlaying={handlePlaying} onClick={handlePlayPause} data-id="element-2934" />
      {/* Loading overlay */}
      {!isLoaded && <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50" data-id="element-2935">
          <div className="w-12 h-12 border-4 border-t-[#2DB489] border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin" data-id="element-2936"></div>
        </div>}
      {/* Buffering indicator */}
      {isBuffering && <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30" data-id="element-2937">
          <div className="w-12 h-12 border-4 border-t-[#2DB489] border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin" data-id="element-2938"></div>
        </div>}
      {/* Play/Pause overlay button (shows when paused) */}
      {!isPlaying && <button className="absolute inset-0 w-full h-full flex items-center justify-center bg-black bg-opacity-30 z-10" onClick={handlePlayPause} aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-2939">
          <div className="w-16 h-16 rounded-full bg-[#2DB489] dark:bg-[#4ED1A0] flex items-center justify-center" data-id="element-2940">
            <PlayIcon className="h-8 w-8 text-white" data-id="element-2941" />
          </div>
        </button>}
      {/* Custom Controls */}
      {showControls && <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-4 py-3 transition-opacity duration-300 ${areControlsVisible || !isPlaying ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()} data-id="element-2942">
          {/* Progress bar — custom touch-friendly */}
          <div ref={progressBarRef} className="relative w-full cursor-pointer mb-2" style={{
        height: '32px',
        touchAction: 'none'
      }} onPointerDown={handleProgressPointerDown} onClick={e => e.stopPropagation()} data-id="element-2943">
            {/* Invisible expanded hit zone — centered vertically */}
            <div className="absolute inset-0" data-id="element-2944" />
            {/* Visible track — centered in hit zone */}
            <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1 bg-gray-500 rounded-full overflow-hidden" data-id="element-2945">
              {/* Filled portion */}
              <div className="h-full bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full" style={{
            width: duration ? `${currentTime / duration * 100}%` : '0%'
          }} data-id="element-2946" />
            </div>
            {/* Thumb */}
            <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[#2DB489] dark:bg-[#4ED1A0]" style={{
          left: duration ? `${currentTime / duration * 100}%` : '0%'
        }} data-id="element-2947" />
          </div>
          <div className="flex items-center justify-between" data-id="element-2948">
            <div className="flex items-center space-x-3" data-id="element-2949">
              {/* Play/Pause button */}
              <button onClick={handlePlayPause} className="text-white hover:text-[#2DB489] dark:hover:text-[#4ED1A0] focus:outline-none" aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-2950">
                {isPlaying ? <PauseIcon className="h-5 w-5" data-id="element-2951" /> : <PlayIcon className="h-5 w-5" data-id="element-2952" />}
              </button>
              {/* Restart button */}
              <button onClick={handleRestart} className="text-white hover:text-[#2DB489] dark:hover:text-[#4ED1A0] focus:outline-none" aria-label="Restart" data-id="element-2953">
                <RotateCcwIcon className="h-4 w-4" data-id="element-2954" />
              </button>
              {/* Volume control — mobile: mute toggle only; desktop: full slider */}
              {'ontouchstart' in window /* Mobile: simple mute/unmute button (iOS cannot control volume programmatically) */ ? <button onClick={e => {
            e.stopPropagation();
            handleMuteToggle();
          }} className="text-white hover:text-[#2DB489] dark:hover:text-[#4ED1A0] focus:outline-none" aria-label={isMuted ? 'Unmute' : 'Mute'} data-id="element-2955">
                  {isMuted ? <VolumeXIcon className="h-5 w-5" data-id="element-2956" /> : <Volume2Icon className="h-5 w-5" data-id="element-2957" />}
                </button> /* Desktop: full volume slider with hover reveal */ : <div className="relative flex items-center" onMouseEnter={() => setShowVolumeSlider(true)} onMouseLeave={() => setShowVolumeSlider(false)} data-id="element-2958">
                  <button onClick={e => {
              e.stopPropagation();
              handleMuteToggle();
            }} className="text-white hover:text-[#2DB489] dark:hover:text-[#4ED1A0] focus:outline-none" aria-label={isMuted ? 'Unmute' : 'Mute'} data-id="element-2959">
                    {isMuted ? <VolumeXIcon className="h-5 w-5" data-id="element-2960" /> : volume > 0.5 ? <Volume2Icon className="h-5 w-5" data-id="element-2961" /> : <VolumeIcon className="h-5 w-5" data-id="element-2962" />}
                  </button>
                  {showVolumeSlider && <div className="absolute left-6 bottom-0 w-24 bg-black bg-opacity-70 rounded-md flex items-center px-2" style={{
              height: '32px',
              touchAction: 'none'
            }} onClick={e => e.stopPropagation()} data-id="element-2963">
                      <div ref={volumeBarRef} className="relative w-full cursor-pointer" style={{
                height: '32px',
                touchAction: 'none'
              }} onPointerDown={handleVolumePointerDown} data-id="element-2964">
                        {/* Visible track */}
                        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1 bg-gray-500 rounded-full overflow-hidden" data-id="element-2965">
                          <div className="h-full bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full" style={{
                    width: `${(isMuted ? 0 : volume) * 100}%`
                  }} data-id="element-2966" />
                        </div>
                        {/* Thumb */}
                        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[#2DB489] dark:bg-[#4ED1A0]" style={{
                  left: `${(isMuted ? 0 : volume) * 100}%`
                }} data-id="element-2967" />
                      </div>
                    </div>}
                </div>}
              {/* Time display */}
              <div className="text-white text-xs" data-id="element-2968">
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>
            </div>
            <div className="flex items-center space-x-3" data-id="element-2969">
              {/* Download button */}
              {allowDownload && <button onClick={handleDownload} className="text-white hover:text-[#2DB489] dark:hover:text-[#4ED1A0] focus:outline-none" aria-label="Download" data-id="element-2970">
                  <DownloadIcon className="h-5 w-5" data-id="element-2971" />
                </button>}
              {/* Fullscreen button */}
              <button onClick={handleFullscreenToggle} className="text-white hover:text-[#2DB489] dark:hover:text-[#4ED1A0] focus:outline-none" aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'} data-id="element-2972">
                {isFullscreen ? <MinimizeIcon className="h-5 w-5" data-id="element-2973" /> : <MaximizeIcon className="h-5 w-5" data-id="element-2974" />}
              </button>
            </div>
          </div>
        </div>}
    </div>;
};